import React, { useState } from 'react';
import { 
  Search, 
  User, 
  Phone, 
  Mail, 
  Wallet, 
  Building,
  Globe, 
  Twitter, 
  Facebook, 
  Instagram, 
  Linkedin,
  AlertTriangle,
  CheckCircle,
  Clock,
  ExternalLink,
  Info,
  Zap,
  Target,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Settings
} from 'lucide-react';
import usernameSearchService, { UsernameSearchResult, SearchOptions } from '../services/usernameSearchService';
import { apiService } from '../services/api';

interface SearchResult {
  id: string;
  type: 'username' | 'phone' | 'email' | 'wallet' | 'company';
  value: string;
  platform: string;
  url?: string;
  status: 'found' | 'not_found' | 'error';
  timestamp: string;
  company?: any;
}

const OSINTSearch: React.FC = () => {
  const [searchType, setSearchType] = useState<'username' | 'phone' | 'email' | 'wallet' | 'company'>('username');
  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [usernameResults, setUsernameResults] = useState<UsernameSearchResult[]>([]);
  const [searchProgress, setSearchProgress] = useState(0);
  const [searchSummary, setSearchSummary] = useState<any>(null);
  const [advancedOptions, setAdvancedOptions] = useState({
    includeNSFW: false,
    selectedPlatforms: [] as string[],
    maxConcurrent: 5,
    timeout: 10000
  });
  const [availablePlatforms, setAvailablePlatforms] = useState<any[]>([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showAdvancedSection, setShowAdvancedSection] = useState(() => {
    const saved = localStorage.getItem('osint-show-advanced-section');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showOnlyFound, setShowOnlyFound] = useState(false);

  // Sauvegarder la préférence de visibilité des options avancées
  React.useEffect(() => {
    localStorage.setItem('osint-show-advanced-section', JSON.stringify(showAdvancedSection));
  }, [showAdvancedSection]);

  // Réinitialiser les options avancées quand le type de recherche change
  React.useEffect(() => {
    let defaultMaxConcurrent = 2;
    let defaultTimeout = 10000;
    
    switch (searchType) {
      case 'username':
        defaultMaxConcurrent = 5;
        defaultTimeout = 10000;
        break;
      case 'email':
        defaultMaxConcurrent = 2;
        defaultTimeout = 25000;
        break;
      case 'phone':
      case 'wallet':
        defaultMaxConcurrent = 2;
        defaultTimeout = 15000;
        break;
      case 'company':
        defaultMaxConcurrent = 1;
        defaultTimeout = 10000;
        break;
    }
    
    setAdvancedOptions(prev => ({
      ...prev,
      maxConcurrent: defaultMaxConcurrent,
      timeout: defaultTimeout,
      selectedPlatforms: [] // Réinitialiser les plateformes sélectionnées
    }));
  }, [searchType]);

  // Charger les plateformes disponibles au montage du composant
  React.useEffect(() => {
    const loadPlatforms = async () => {
      try {
        const platforms = await usernameSearchService.getAvailablePlatforms();
        setAvailablePlatforms(platforms);
      } catch (error) {
        console.error('Erreur chargement plateformes:', error);
      }
    };
    loadPlatforms();
  }, []);

  // Plateformes automatiques selon le type de recherche
  const getAutomaticPlatforms = (type: string) => {
    switch (type) {
      case 'username':
        return [
          { id: 'twitter', name: 'Twitter', icon: Twitter, color: 'text-blue-400' },
          { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'text-pink-400' },
          { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'text-blue-600' },
          { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'text-blue-700' },
          { id: 'github', name: 'GitHub', icon: Globe, color: 'text-gray-400' },
          { id: 'reddit', name: 'Reddit', icon: Globe, color: 'text-orange-500' }
        ];
      case 'phone':
        return [
          { id: 'truecaller', name: 'Truecaller', icon: Phone, color: 'text-green-500' },
          { id: 'whitepages', name: 'WhitePages', icon: Phone, color: 'text-blue-500' },
          { id: 'social', name: 'Réseaux Sociaux', icon: Globe, color: 'text-purple-500' }
        ];
      case 'email':
        return [
          { id: 'haveibeenpwned', name: 'HaveIBeenPwned', icon: Mail, color: 'text-red-500' },
          { id: 'github', name: 'GitHub Email', icon: Globe, color: 'text-gray-400' },
          { id: 'gravatar', name: 'Gravatar', icon: User, color: 'text-blue-500' },
          { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'text-pink-400' },
          { id: 'twitter', name: 'Twitter', icon: Twitter, color: 'text-blue-400' },
          { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'text-blue-700' },
          { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'text-blue-600' },
          { id: 'discord', name: 'Discord', icon: Globe, color: 'text-indigo-500' },
          { id: 'skype', name: 'Skype', icon: Globe, color: 'text-cyan-500' },
          { id: 'adobe', name: 'Adobe', icon: Globe, color: 'text-red-600' },
          { id: 'pinterest', name: 'Pinterest', icon: Globe, color: 'text-red-500' }
        ];
      case 'wallet':
        return [
          { id: 'etherscan', name: 'Etherscan', icon: Wallet, color: 'text-blue-400' },
          { id: 'blockchain', name: 'Blockchain.info', icon: Wallet, color: 'text-orange-500' },
          { id: 'social', name: 'Réseaux Sociaux', icon: Globe, color: 'text-purple-500' }
        ];
      case 'company':
        return [
          { id: 'sirene', name: 'SIRENE (INSEE)', icon: Building, color: 'text-blue-600' },
          { id: 'infogreffe', name: 'Infogreffe', icon: Building, color: 'text-green-600' },
          { id: 'societe', name: 'Societe.com', icon: Building, color: 'text-purple-600' },
          { id: 'verif', name: 'Verif.com', icon: Building, color: 'text-orange-600' },
          { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'text-blue-700' },
          { id: 'social', name: 'Réseaux Sociaux', icon: Globe, color: 'text-purple-500' }
        ];
      default:
        return [];
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) return;

    setIsSearching(true);
    setResults([]);
    setUsernameResults([]);
    setSearchProgress(0);
    setSearchSummary(null);

    if (searchType === 'username') {
      await handleUsernameSearch();
    } else {
      await handleOtherSearch();
    }

    setIsSearching(false);
  };

  const handleUsernameSearch = async () => {
    try {
      const options: SearchOptions = {
        platforms: advancedOptions.selectedPlatforms.length > 0 ? advancedOptions.selectedPlatforms : undefined,
        includeNSFW: advancedOptions.includeNSFW,
        timeout: advancedOptions.timeout,
        maxConcurrent: advancedOptions.maxConcurrent
      };

      // Simuler le progrès de recherche
      const progressInterval = setInterval(() => {
        setSearchProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const searchResults = await usernameSearchService.searchUsername(searchValue, options);
      
      clearInterval(progressInterval);
      setSearchProgress(100);

      setUsernameResults(searchResults);
      
      // Générer le résumé
      const summary = usernameSearchService.generateSummaryReport(searchResults);
      setSearchSummary(summary);

    } catch (error) {
      console.error('Username search error:', error);
      setSearchProgress(0);
    }
  };

  const handleOtherSearch = async () => {
    try {
      let endpoint = '';
      let payload = {};
      
      switch (searchType) {
        case 'email':
          endpoint = '/osint/search/email';
          payload = {
            email: searchValue,
            includeMetadata: true,
            timeout: 25000,
            maxConcurrent: 2
          };
          break;
        case 'phone':
          endpoint = '/osint/search/phone';
          payload = {
            phoneNumber: searchValue,
            includeMetadata: true,
            timeout: 15000,
            maxConcurrent: 2
          };
          break;
        case 'wallet':
          endpoint = '/osint/search/crypto';
          payload = {
            address: searchValue,
            includeMetadata: true,
            timeout: 15000,
            maxConcurrent: 2
          };
          break;
        case 'company':
          // Déterminer si c'est un SIRET ou un nom d'entreprise
          const isNumeric = /^\d+$/.test(searchValue);
          endpoint = isNumeric ? '/osint/search/company/siret' : '/osint/search/company/name';
          payload = isNumeric ? 
            { siret: searchValue, includeMetadata: true } :
            { companyName: searchValue, includeMetadata: true };
          break;
        default:
          throw new Error(`Type de recherche non supporté: ${searchType}`);
      }

      console.log(`🔍 Début de la recherche ${searchType} OSINT pour:`, searchValue);
      
      const response = await apiService.post(endpoint, payload);
      const data = response as any;
      setSearchProgress(100);
      
      // Convertir les résultats au format attendu par l'interface
      const searchResults = data.data.results ? data.data.results.map((result: any) => ({
        id: `${result.platform}-${Date.now()}`,
        type: searchType,
        value: searchValue,
        platform: result.platform,
        url: result.url,
        status: result.status,
        timestamp: new Date().toISOString()
      })) : [];

      // Pour les entreprises, adapter le format
      if (searchType === 'company' && data.data.company) {
        const companyResult = [{
          id: `company-${Date.now()}`,
          type: searchType,
          value: searchValue,
          platform: 'INSEE',
          url: `https://www.insee.fr/fr/metadonnees/source/serie/s1315`,
          status: 'found' as const,
          timestamp: new Date().toISOString(),
          company: data.data.company
        }];
        setResults(companyResult);
      } else if (searchType === 'company' && data.data.companies) {
        const companyResults = data.data.companies.map((company: any, index: number) => ({
          id: `company-${index}-${Date.now()}`,
          type: searchType,
          value: searchValue,
          platform: 'INSEE',
          url: `https://www.insee.fr/fr/metadonnees/source/serie/s1315`,
          status: 'found' as const,
          timestamp: new Date().toISOString(),
          company: company
        }));
        setResults(companyResults);
      } else {
        setResults(searchResults);
      }
      
      setSearchSummary(data.data.summary);
      
    } catch (error) {
      console.error(`Erreur recherche ${searchType}:`, error);
      setSearchProgress(0);
      setResults([]);
      
      // Fallback vers l'ancien système de simulation
      if (searchType !== 'email') {
      // Simulation de recherche OSINT automatique sur toutes les plateformes (ancien code)
      const mockResults: SearchResult[] = [];
      const currentPlatforms = getAutomaticPlatforms(searchType);

      for (const platform of currentPlatforms) {
        setSearchProgress(prev => Math.min(prev + (100 / currentPlatforms.length), 100));
        
        // Simulation de délai pour chaque plateforme
        await new Promise(resolve => setTimeout(resolve, Math.random() * 2000 + 500));

        const isFound = Math.random() > 0.3; // 70% de chance de trouver
        const status: 'found' | 'not_found' | 'error' = isFound ? 'found' : 'not_found';

        mockResults.push({
          id: `${platform.id}-${Date.now()}`,
          type: searchType,
          value: searchValue,
          platform: platform.name,
          url: isFound ? `https://${platform.id}.com/${searchValue}` : undefined,
          status,
          timestamp: new Date().toISOString()
        });
      }

      setResults(mockResults);
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'found':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'not_found':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'found':
        return 'Trouvé';
      case 'not_found':
        return 'Non trouvé';
      case 'error':
        return 'Erreur';
      default:
        return 'En cours...';
    }
  };

  const getSearchPlaceholder = () => {
    switch (searchType) {
      case 'username':
        return 'Ex: john_doe, @johndoe, john.doe';
      case 'phone':
        return 'Ex: +33 1 23 45 67 89, 0123456789';
      case 'email':
        return 'Ex: john.doe@example.com';
      case 'wallet':
        return 'Ex: 0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6';
      case 'company':
        return 'Ex: 12345678901234 (SIRET) ou nom de la société';
      default:
        return 'Entrez la valeur à rechercher';
    }
  };

  const getSearchDescription = () => {
    switch (searchType) {
      case 'username':
        return 'Recherche intelligente type Sherlock sur 20+ plateformes avec détection anti-faux positifs';
      case 'phone':
        return 'Recherche automatique sur Truecaller, WhitePages, réseaux sociaux';
      case 'email':
        return 'Recherche approfondie sur HaveIBeenPwned, réseaux sociaux, Gravatar, services cloud et 10+ plateformes';
      case 'wallet':
        return 'Recherche automatique sur Etherscan, Blockchain.info, réseaux sociaux';
      case 'company':
        return 'Recherche automatique sur SIRENE, Infogreffe, Societe.com, Verif.com, LinkedIn';
      default:
        return 'Recherche automatique sur toutes les plateformes pertinentes';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-400 bg-green-400/10';
    if (confidence >= 0.6) return 'text-yellow-400 bg-yellow-400/10';
    if (confidence >= 0.4) return 'text-orange-400 bg-orange-400/10';
    return 'text-red-400 bg-red-400/10';
  };

  const getConfidenceText = (confidence: number) => {
    if (confidence >= 0.8) return 'Très fiable';
    if (confidence >= 0.6) return 'Fiable';
    if (confidence >= 0.4) return 'Moyen';
    return 'Faible';
  };

  // Filtrer les résultats selon les options
  const getFilteredResults = () => {
    if (showOnlyFound) {
      return results.filter(r => r.status === 'found');
    }
    return results;
  };

  const getFilteredUsernameResults = () => {
    if (showOnlyFound) {
      return usernameResults.filter(r => r.status === 'found');
    }
    return usernameResults;
  };

  // Obtenir les options avancées selon le type de recherche
  const getAdvancedOptionsForSearchType = () => {
    switch (searchType) {
      case 'username':
        return {
          showPlatforms: true,
          showTimeout: true,
          showMaxConcurrent: true,
          showNSFW: true,
          defaultTimeout: 10000,
          maxTimeout: 30000,
          defaultMaxConcurrent: 5,
          maxMaxConcurrent: 10
        };
      case 'email':
        return {
          showPlatforms: false,
          showTimeout: true,
          showMaxConcurrent: true,
          showNSFW: false,
          defaultTimeout: 25000,
          maxTimeout: 60000,
          defaultMaxConcurrent: 2,
          maxMaxConcurrent: 3
        };
      case 'phone':
        return {
          showPlatforms: false,
          showTimeout: true,
          showMaxConcurrent: true,
          showNSFW: false,
          defaultTimeout: 15000,
          maxTimeout: 30000,
          defaultMaxConcurrent: 2,
          maxMaxConcurrent: 3
        };
      case 'wallet':
        return {
          showPlatforms: false,
          showTimeout: true,
          showMaxConcurrent: true,
          showNSFW: false,
          defaultTimeout: 15000,
          maxTimeout: 30000,
          defaultMaxConcurrent: 2,
          maxMaxConcurrent: 3
        };
      case 'company':
        return {
          showPlatforms: false,
          showTimeout: false,
          showMaxConcurrent: false,
          showNSFW: false,
          defaultTimeout: 10000,
          maxTimeout: 20000,
          defaultMaxConcurrent: 1,
          maxMaxConcurrent: 1
        };
      default:
        return {
          showPlatforms: false,
          showTimeout: true,
          showMaxConcurrent: true,
          showNSFW: false,
          defaultTimeout: 10000,
          maxTimeout: 30000,
          defaultMaxConcurrent: 2,
          maxMaxConcurrent: 5
        };
    }
  };

  const renderAdvancedOptions = () => {
    if (!showAdvancedSection) return null;
    
    const options = getAdvancedOptionsForSearchType();

    return (
      <div className="osint-card p-6 rounded-lg mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-osint-orange" />
            <h3 className="text-lg font-semibold text-white">Options avancées</h3>
            <span className="text-sm text-gray-400">({searchType})</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              className="flex items-center space-x-2 text-osint-orange hover:text-orange-400 transition-colors"
            >
              {showAdvancedOptions ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  <span className="text-sm">Masquer</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  <span className="text-sm">Afficher</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowAdvancedSection(false)}
              className="flex items-center space-x-1 text-gray-400 hover:text-red-400 transition-colors text-sm px-2 py-1 rounded hover:bg-red-400/10"
              title="Masquer complètement la section options avancées"
            >
              <span className="text-xs">✕</span>
              <span className="text-xs">Masquer</span>
            </button>
          </div>
        </div>

        {showAdvancedOptions && (
          <div className="space-y-4">
            {/* Sélection des plateformes - uniquement pour username */}
            {options.showPlatforms && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Plateformes à rechercher (laisser vide pour toutes)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                  {availablePlatforms.map(platform => (
                    <label key={platform.name} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={advancedOptions.selectedPlatforms.includes(platform.name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAdvancedOptions(prev => ({
                              ...prev,
                              selectedPlatforms: [...prev.selectedPlatforms, platform.name]
                            }));
                          } else {
                            setAdvancedOptions(prev => ({
                              ...prev,
                              selectedPlatforms: prev.selectedPlatforms.filter(p => p !== platform.name)
                            }));
                          }
                        }}
                        className="rounded border-gray-600 text-osint-orange focus:ring-osint-orange"
                      />
                      <span className="text-gray-300">{platform.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Options configurables */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* NSFW */}
              {options.showNSFW && (
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={advancedOptions.includeNSFW}
                      onChange={(e) => setAdvancedOptions(prev => ({ ...prev, includeNSFW: e.target.checked }))}
                      className="rounded border-gray-600 text-osint-orange focus:ring-osint-orange"
                    />
                    <span className="text-gray-300">Inclure contenu NSFW</span>
                  </label>
                </div>
              )}

              {/* Requêtes simultanées */}
              {options.showMaxConcurrent && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Requêtes simultanées: {advancedOptions.maxConcurrent}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max={options.maxMaxConcurrent}
                    value={advancedOptions.maxConcurrent}
                    onChange={(e) => setAdvancedOptions(prev => ({ ...prev, maxConcurrent: parseInt(e.target.value) }))}
                    className="w-full"
                  />
                </div>
              )}

              {/* Timeout */}
              {options.showTimeout && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Timeout: {advancedOptions.timeout / 1000}s
                  </label>
                  <input
                    type="range"
                    min="5000"
                    max={options.maxTimeout}
                    step="1000"
                    value={advancedOptions.timeout}
                    onChange={(e) => setAdvancedOptions(prev => ({ ...prev, timeout: parseInt(e.target.value) }))}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Recherches OSINT</h2>
        <div className="flex items-center space-x-2">
          <Search className="w-5 h-5 text-osint-orange" />
          <span className="text-sm text-gray-400">Recherche automatisée</span>
        </div>
      </div>

      {/* Sélection du type de recherche */}
      <div className="osint-card p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-white mb-4">Type de recherche</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { type: 'username', label: 'Nom d\'utilisateur', icon: User },
            { type: 'phone', label: 'Numéro de téléphone', icon: Phone },
            { type: 'email', label: 'Adresse email', icon: Mail },
            { type: 'wallet', label: 'Wallet (BTC/ETH)', icon: Wallet },
            { type: 'company', label: 'Société (SIRET)', icon: Building }
          ].map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              onClick={() => setSearchType(type as any)}
              className={`p-4 rounded-lg border-2 transition-all ${
                searchType === type
                  ? 'border-osint-orange bg-osint-orange/10'
                  : 'border-osint-light-gray hover:border-osint-orange/50'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <Icon className={`w-6 h-6 ${searchType === type ? 'text-osint-orange' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${searchType === type ? 'text-osint-orange' : 'text-gray-400'}`}>
                  {label}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Options avancées adaptées au type de recherche */}
      {renderAdvancedOptions()}

      {/* Champ de recherche simplifié */}
      <div className="osint-card p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-white">Valeur à rechercher</h3>
            <div className="flex items-center space-x-1 text-gray-400">
              {searchType === 'username' ? <Zap className="w-4 h-4" /> : <Info className="w-4 h-4" />}
              <span className="text-sm">
                {searchType === 'username' ? 'Recherche intelligente' : 'Recherche automatique'}
              </span>
            </div>
          </div>
          {!showAdvancedSection && (
            <button
              onClick={() => setShowAdvancedSection(true)}
              className="flex items-center space-x-2 text-gray-400 hover:text-osint-orange transition-colors text-sm"
              title="Afficher les options avancées"
            >
              <Settings className="w-4 h-4" />
              <span>Options avancées</span>
            </button>
          )}
        </div>
        
        <div className="mb-4 p-3 bg-osint-light-gray rounded-lg">
          <p className="text-sm text-gray-300">{getSearchDescription()}</p>
        </div>

        <div className="flex space-x-4">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={getSearchPlaceholder()}
            className="osint-input flex-1 px-4 py-3 rounded-lg text-white placeholder-gray-400 focus:outline-none"
            onKeyPress={(e) => e.key === 'Enter' && !isSearching && searchValue.trim() && handleSearch()}
          />
          <button
            onClick={handleSearch}
            disabled={isSearching || !searchValue.trim()}
            className="osint-button px-6 py-3 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSearching ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Recherche...</span>
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                <span>Rechercher</span>
              </>
            )}
          </button>
        </div>

        {/* Barre de progression */}
        {isSearching && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-300">Progression</span>
              <span className="text-sm text-gray-300">{searchProgress}%</span>
            </div>
            <div className="w-full bg-osint-light-gray rounded-full h-2">
              <div 
                className="bg-osint-orange h-2 rounded-full transition-all duration-300" 
                style={{ width: `${searchProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Statistiques de recherche - Affichage en haut après recherche */}
      {(results.length > 0 || usernameResults.length > 0) && (
        <div className="osint-card p-6 rounded-lg mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <TrendingUp className="w-5 h-5 text-osint-orange mr-2" />
              Statistiques de la recherche
            </h3>
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={showOnlyFound}
                  onChange={(e) => setShowOnlyFound(e.target.checked)}
                  className="rounded border-gray-600 text-osint-orange focus:ring-osint-orange"
                />
                <span className="text-sm text-gray-300">Afficher uniquement les résultats trouvés</span>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-osint-light-gray rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-white font-medium">
                  {results.length > 0 
                    ? results.filter(r => r.status === 'found').length
                    : usernameResults.filter(r => r.status === 'found').length
                  }
                </p>
                <p className="text-gray-400 text-sm">Trouvés</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-osint-light-gray rounded-lg">
              <AlertTriangle className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-white font-medium">
                  {results.length > 0 
                    ? results.filter(r => r.status === 'not_found').length
                    : usernameResults.filter(r => r.status === 'not_found').length
                  }
                </p>
                <p className="text-gray-400 text-sm">Non trouvés</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-osint-light-gray rounded-lg">
              <Globe className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-white font-medium">
                  {results.length > 0 ? results.length : usernameResults.length}
                </p>
                <p className="text-gray-400 text-sm">Plateformes testées</p>
              </div>
            </div>
          </div>
          {searchSummary && (
            <div className="mt-4 p-3 bg-osint-light-gray rounded-lg">
              <p className="text-gray-300 text-sm">
                <strong>Résumé:</strong> {searchSummary.found} profils trouvés sur {searchSummary.total} plateformes 
                {searchSummary.averageResponseTime && ` (temps moyen: ${searchSummary.averageResponseTime}ms)`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Résultats */}
      {results.length > 0 && (
        <div className="osint-card p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-4">
            Résultats de la recherche
            {showOnlyFound && (
              <span className="ml-2 text-sm text-osint-orange">
                (filtré: {getFilteredResults().length} sur {results.length})
              </span>
            )}
          </h3>
          <div className="space-y-4">
            {getFilteredResults().length === 0 && showOnlyFound ? (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <p className="text-gray-300">Aucun résultat trouvé avec le filtre actuel</p>
                <p className="text-sm text-gray-400 mt-2">
                  Désactivez le filtre pour voir tous les résultats
                </p>
              </div>
            ) : (
              getFilteredResults().map((result) => (
              <div key={result.id} className="border border-osint-light-gray rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(result.status)}
                    <div>
                      <p className="text-white font-medium">{result.platform}</p>
                      <p className="text-gray-400 text-sm">{result.value}</p>
                      {/* Afficher les détails de l'entreprise si disponible */}
                      {result.company && (
                        <div className="mt-2 text-xs text-gray-400">
                          <p><strong>Dénomination:</strong> {result.company.denomination}</p>
                          <p><strong>Adresse:</strong> {result.company.adresse}, {result.company.code_postal} {result.company.ville}</p>
                          <p><strong>SIRET:</strong> {result.company.siret}</p>
                          <p><strong>Activité:</strong> {result.company.activite_principale}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm px-2 py-1 rounded ${
                      result.status === 'found' ? 'bg-green-600/20 text-green-400' :
                      result.status === 'not_found' ? 'bg-yellow-600/20 text-yellow-400' :
                      'bg-red-600/20 text-red-400'
                    }`}>
                      {getStatusText(result.status)}
                    </span>
                    {result.url && (
                      <a
                        href={result.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-osint-orange hover:text-orange-400"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Recherché le {new Date(result.timestamp).toLocaleString('fr-FR')}
                </div>
              </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Résultats de recherche par nom d'utilisateur */}
      {usernameResults.length > 0 && (
        <>
          {/* Résultats détaillés */}
          <div className="osint-card p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Résultats détaillés
                {showOnlyFound && (
                  <span className="ml-2 text-sm text-osint-orange">
                    (filtré: {getFilteredUsernameResults().length} sur {usernameResults.length})
                  </span>
                )}
              </h3>
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4 text-osint-orange" />
                <span className="text-sm text-gray-400">Triés par fiabilité</span>
              </div>
            </div>

            <div className="space-y-3">
              {getFilteredUsernameResults().length === 0 && showOnlyFound ? (
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                  <p className="text-gray-300">Aucun résultat trouvé avec le filtre actuel</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Désactivez le filtre pour voir tous les résultats
                  </p>
                </div>
              ) : (
                getFilteredUsernameResults().map((result, index) => (
                <div key={`${result.platform}-${index}`} className="border border-osint-light-gray rounded-lg p-4 hover:border-osint-orange/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(result.status)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="text-white font-medium">{result.platform}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(result.confidence)}`}>
                            {getConfidenceText(result.confidence)} ({Math.round(result.confidence * 100)}%)
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm">@{result.username}</p>
                        {result.error_details && (
                          <p className="text-gray-500 text-xs mt-1">{result.error_details}</p>
                        )}
                        {result.response_time && (
                          <p className="text-gray-500 text-xs">Temps de réponse: {result.response_time}ms</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm px-3 py-1 rounded-full ${
                        result.status === 'found' ? 'bg-green-600/20 text-green-400' :
                        result.status === 'not_found' ? 'bg-yellow-600/20 text-yellow-400' :
                        'bg-red-600/20 text-red-400'
                      }`}>
                        {result.status === 'found' ? 'Trouvé' : 
                         result.status === 'not_found' ? 'Non trouvé' : 'Erreur'}
                      </span>
                      
                      {result.url && result.status !== 'found' && (
                        <span className="text-xs px-2 py-1 rounded-full bg-yellow-600/20 text-yellow-400">Profil à vérifier</span>
                      )}
                      {result.url && (
                        <a
                          href={result.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-osint-orange hover:text-orange-400 p-1 rounded hover:bg-osint-orange/10 transition-colors"
                          title="Ouvrir le profil"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default OSINTSearch; 